import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  acknowledgeLegalCounsel,
  fetchLegalDocumentText,
  getProductionReadiness,
} from '../../api/productionReadiness';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import Button from '../../components/common/Button';
import { TextInput } from '../../components/common/form';
import { apiErrorMessage } from '../../utils/apiError';
import { useRegionalSettings } from '../../context/RegionalSettingsContext';
import { isEuProductionReadinessCountry } from '../../config/countryProfiles';

const LEGAL_DOCS = [
  { id: 'privacy-eu', labelKey: 'settings.production.docPrivacyEu' },
  { id: 'dpa-eu', labelKey: 'settings.production.docDpaEu' },
  { id: 'privacy', labelKey: 'settings.production.docPrivacy' },
  { id: 'dpa', labelKey: 'settings.production.docDpa' },
  { id: 'terms', labelKey: 'settings.production.docTerms' },
  { id: 'eula', labelKey: 'settings.production.docEula' },
];

function Flag({ ok }) {
  return (
    <span
      className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
        ok ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'
      }`}
      aria-hidden
    >
      {ok ? '✓' : '!'}
    </span>
  );
}

export default function ProductionReadiness() {
  const { t } = useTranslation();
  const { settings: regionalSettings, ready: regionalReady } = useRegionalSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [notes, setNotes] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [acking, setAcking] = useState(false);
  const [docId, setDocId] = useState('privacy-eu');
  const [docText, setDocText] = useState('');
  const [docLoading, setDocLoading] = useState(false);

  const euEligible = regionalReady && isEuProductionReadinessCountry(regionalSettings?.countryCode);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const d = await getProductionReadiness();
      setData(d);
    } catch {
      setError(t('settings.production.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!euEligible) return undefined;
    load();
    return undefined;
  }, [euEligible]);

  useEffect(() => {
    if (!euEligible) return undefined;
    let active = true;
    setDocLoading(true);
    fetchLegalDocumentText(docId)
      .then((text) => {
        if (active) setDocText(text);
      })
      .catch(() => {
        if (active) setDocText(t('settings.production.docLoadFailed'));
      })
      .finally(() => {
        if (active) setDocLoading(false);
      });
    return () => {
      active = false;
    };
  }, [docId, t, euEligible]);

  const acknowledge = async () => {
    if (!confirm) {
      setError(t('settings.production.confirmRequired'));
      return;
    }
    setAcking(true);
    setError('');
    setSuccess('');
    try {
      await acknowledgeLegalCounsel({ confirm: true, notes: notes.trim() || undefined });
      setSuccess(t('settings.production.ackSuccess'));
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, t('settings.production.ackFailed')));
    } finally {
      setAcking(false);
    }
  };

  if (!regionalReady) {
    return (
      <PageLoadingShell
        variant="page-shell-form"
        title={t('settings.production.title')}
        subtitle={t('settings.production.loadingSubtitle')}
      />
    );
  }

  if (!euEligible) {
    return <Navigate to="/admin" replace />;
  }

  if (loading) {
    return (
      <PageLoadingShell
        variant="page-shell-form"
        title={t('settings.production.title')}
        subtitle={t('settings.production.loadingSubtitle')}
      />
    );
  }

  if (!data) {
    return (
      <div className="page-shell-form max-w-3xl">
        <PageHeaderSection
          eyebrow={t('nav.system')}
          title={t('settings.production.title')}
          subtitle={t('settings.production.subtitle')}
        />
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          {error || t('settings.production.loadFailed')}
        </div>
        <Button type="button" variant="page-primary" onClick={load}>
          {t('common.retry', { defaultValue: 'Try again' })}
        </Button>
      </div>
    );
  }

  const checks = [
    {
      ok: data.productionHttps,
      label: t('settings.production.checkHttps'),
      detail: data.publicBaseUrl || '—',
      link: '/admin/payment-methods',
    },
    {
      ok: data.mollieWebhookConfigured,
      label: t('settings.production.checkMollieWebhook'),
      detail: data.webhookUrls?.mollie || '—',
      soft: true,
    },
    {
      ok: data.smtpOperational,
      label: t('settings.production.checkSmtp'),
      detail: data.smtpLastTestOk === false
        ? t('settings.production.smtpLastTestFailed')
        : data.smtpLastTestAt || '',
      link: '/admin/email-settings',
    },
    {
      ok: !data.whatsappLicensed || data.whatsappOperational,
      label: t('settings.production.checkWhatsapp'),
      detail: data.whatsappLocaleSuffix
        ? t('settings.production.whatsappSuffix', { suffix: data.whatsappLocaleSuffix })
        : t('settings.production.whatsappEnglish'),
      link: '/admin/whatsapp',
      soft: !data.whatsappLicensed,
    },
    {
      ok: data.legalCounselAcknowledged,
      label: t('settings.production.checkLegal'),
      detail: data.legalDocumentVersion || '',
    },
    {
      ok: Boolean(data.academyTimezone),
      label: t('settings.production.checkTimezone'),
      detail: t('settings.production.timezoneDetail', {
        academy: data.academyTimezone,
      }),
      link: '/admin/regional-settings',
      soft: true,
    },
  ];

  return (
    <div className="page-shell-form">
      <PageHeaderSection
        eyebrow={t('nav.system')}
        title={t('settings.production.title')}
        subtitle={t('settings.production.subtitle')}
      />

      {success && (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div
        className={`mb-6 rounded-2xl border px-5 py-4 ${
          data.readyForEuProduction
            ? 'border-emerald-200 bg-emerald-50'
            : 'border-amber-200 bg-amber-50'
        }`}
      >
        <p className="text-sm font-semibold text-slate-900">
          {data.readyForEuProduction
            ? t('settings.production.readyBanner')
            : t('settings.production.notReadyBanner')}
        </p>
        <p className="mt-1 text-sm text-slate-700">{t('settings.production.readyHint')}</p>
      </div>

      {(data.blockers?.length > 0 || data.warnings?.length > 0) && (
        <section className="mb-6 space-y-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          {data.blockers?.map((b) => (
            <p key={b} className="text-sm text-red-700">
              • {b}
            </p>
          ))}
          {data.warnings?.map((w) => (
            <p key={w} className="text-sm text-amber-800">
              • {w}
            </p>
          ))}
        </section>
      )}

      <section className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">{t('settings.production.checklistTitle')}</h2>
        </div>
        <ul className="divide-y divide-slate-100">
          {checks.map((c) => (
            <li key={c.label} className="flex flex-wrap items-start gap-3 px-5 py-4">
              <Flag ok={c.ok} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900">{c.label}</p>
                {c.detail && (
                  <p className="mt-0.5 break-all font-mono text-xs text-slate-500">{c.detail}</p>
                )}
              </div>
              {c.link && (
                <Link to={c.link} className="text-sm font-medium text-primary-700 hover:underline">
                  {t('settings.production.open')}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </section>

      {data.webhookUrls && Object.keys(data.webhookUrls).length > 0 && (
        <section className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
            <h2 className="text-lg font-semibold text-slate-900">{t('settings.production.webhooksTitle')}</h2>
            <p className="mt-1 text-sm text-slate-600">{t('settings.production.webhooksDesc')}</p>
          </div>
          <dl className="divide-y divide-slate-100">
            {Object.entries(data.webhookUrls).map(([key, url]) => (
              <div key={key} className="grid gap-1 px-5 py-3 sm:grid-cols-[8rem_1fr]">
                <dt className="text-sm font-medium capitalize text-slate-700">{key}</dt>
                <dd className="break-all font-mono text-xs text-slate-600">{url}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {data.whatsappLocalizedTemplates?.length > 0 && (
        <section className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
            <h2 className="text-lg font-semibold text-slate-900">{t('settings.production.templatesTitle')}</h2>
            <p className="mt-1 text-sm text-slate-600">{t('settings.production.templatesDesc')}</p>
          </div>
          <ul className="grid gap-1 px-5 py-4 font-mono text-xs text-slate-700 sm:grid-cols-2">
            {data.whatsappLocalizedTemplates.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">{t('settings.production.legalTitle')}</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">{t('settings.production.legalDesc')}</p>
        </div>
        <div className="flex flex-wrap gap-2 border-b border-slate-100 px-5 py-3">
          {LEGAL_DOCS.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setDocId(d.id)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                docId === d.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {t(d.labelKey)}
            </button>
          ))}
        </div>
        <pre className="max-h-80 overflow-auto whitespace-pre-wrap px-5 py-4 text-xs leading-relaxed text-slate-700">
          {docLoading ? t('settings.production.docLoading') : docText}
        </pre>
        <div className="space-y-4 border-t border-slate-100 px-5 py-5">
          {data.legalCounselAcknowledged ? (
            <p className="text-sm text-emerald-800">
              {t('settings.production.alreadyAcked', {
                by: data.legalCounselAcknowledgedBy,
                at: data.legalCounselAcknowledgedAt,
                version: data.legalDocumentVersion,
              })}
            </p>
          ) : (
            <>
              <label className="flex items-start gap-3 text-sm text-slate-800">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={confirm}
                  onChange={(e) => setConfirm(e.target.checked)}
                />
                <span>{t('settings.production.confirmLabel')}</span>
              </label>
              <TextInput
                label={t('settings.production.notesLabel')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('settings.production.notesPlaceholder')}
              />
              <Button onClick={acknowledge} disabled={acking || !confirm}>
                {acking ? t('forms.common.saving') : t('settings.production.ackButton')}
              </Button>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
